import store from "@/store";
import {
  setAutoLoader,
  setLoader,
  showNotification,
} from "@/store/app/reducer";
import { resetUserContext } from "@/store/auth/reducer";
import axios, { Axios, AxiosResponse, ResponseType } from "axios";
import i18next from "i18next";
import { removeLocalStorageItems } from "@/common/utils/utils";

class HttpClient {
  private axiosInstance: Axios;
  private activeRequests: number = 0;

  constructor(apiVersion: string | undefined) {
    this.axiosInstance = new axios.Axios({
      baseURL: process.env.REACT_APP_API_BASE_URL + "/" + apiVersion,
    });

    this.axiosInstance.interceptors.request.use(
      (request) => {
        const token = localStorage.getItem("access_token");
        // const origin = window.location.origin;
        request.headers = {
          ...request.headers,
          Authorization: `Bearer ${token}`,
          "content-type": "application/json",
          // Origin: origin,
        } as any;
        this.activeRequests++;

        if (!request.headers["X-Hide-Loading-Screen"]) {
          // store.store.dispatch(setLoader(true));
        } else {
          // store.dispatch(setAutoLoader(true));
        }
        return request;
      },
      undefined,
      { synchronous: true }
    );

    this.axiosInstance.interceptors.response.use(
      (response) => {
        this.decrementLoader();

        if (response.status >= 400) {
          if (response.status === 401) {
            store.dispatch(
              showNotification({
                showNotification: true,
                message:
                  response.statusText ||
                  i18next.t("chargers.action_error.unauthorized"),
                type: "error",
              })
            );
            store.dispatch(resetUserContext());
            removeLocalStorageItems();
          } else if (response.status === 595) {
            store.dispatch(
              showNotification({
                showNotification: true,
                message: i18next.t("general.user_or_tenant_updated"),
                type: "warning",
              })
            );
            store.dispatch(resetUserContext());
            removeLocalStorageItems();
          } else if (response.status === 596) {
            store.dispatch(
              showNotification({
                showNotification: true,
                message: i18next.t("general.user_or_tenant_updated"),
                type: "warning",
              })
            );
            store.dispatch(resetUserContext());
            removeLocalStorageItems();
          }
          return response;
        } else {
          try {
            return JSON.parse(response.data);
          } catch (error) {
            return response.data;
          }
        }
      },
      (error) => {
        this.decrementLoader();
        return Promise.reject(error);
      },
      { synchronous: true }
    );
  }

  private decrementLoader() {
    this.activeRequests = Math.max(0, this.activeRequests - 1);

    if (this.activeRequests === 0) {
      store.dispatch(setLoader(false));
      store.dispatch(setAutoLoader(false));
    }
  }

  get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    return this.axiosInstance.get<any, T>(endpoint, {
      params,
    });
  }

  getText<T>(
    endpoint: string,
    params?: Record<string, any>
  ): Promise<T> {
    return this.axiosInstance.get<any, T>(endpoint, {
      responseType: 'text',
      params,
    });
  }

  patch<T>(
    endpoint: string,
    body?: any,
    params?: Record<string, any>
  ): Promise<AxiosResponse<T, any>> {
    return this.axiosInstance.patch<T>(endpoint, JSON.stringify(body), {
      params,
    });
  }

  getQRCode<T>(
    endpoint: string,
    params?: Record<string, any>,
    headers: any = {},
    responseType: ResponseType = "json"
  ): Promise<T> {
    return this.axiosInstance.get<any, T>(endpoint, {
      responseType: responseType,
      params,
      headers,
    });
  }
  getImage<T>(
    endpoint: string,
    params?: Record<string, any>,
    headers: any = {},
    responseType: ResponseType = "blob"
  ): Promise<T> {
    return this.axiosInstance.get<any, T>(endpoint, {
      responseType: responseType,
      params,
      headers,
    });
  }

  uploadFile<T>(
    endpoint: string,
    body: any,
    {
      params,
      headers = {},
    }: { params?: Record<string, any>; headers: Record<string, any> }
  ) {
    return this.axiosInstance.post<T>(endpoint, body, {
      params,
      headers,
    });
  }

  post<T>(
    endpoint: string,
    body?: any,
    params?: Record<string, any>
  ): Promise<AxiosResponse<T, any>> {
    return this.axiosInstance.post<T>(endpoint, JSON.stringify(body), {
      params,
    });
  }

  delete<T>(
    endpoint: string,
    params?: Record<string, any>
  ): Promise<AxiosResponse<T, any>> {
    return this.axiosInstance.delete<T>(endpoint, {
      params,
    });
  }

  deleteV2<T>(
    endpoint: string,
    {
      params,
      headers = {},
      body,
    }: {
      params?: Record<string, any>;
      headers: Record<string, any>;
      body?: Record<string, any>;
    }
  ) {
    return this.axiosInstance.delete<T>(endpoint, {
      params,
      headers,
      data: JSON.stringify(body),
    });
  }

  put<T>(
    endpoint: string,
    body?: any,
    params?: Record<string, any>
  ): Promise<AxiosResponse<T, any>> {
    return this.axiosInstance.put<T>(endpoint, JSON.stringify(body), {
      params,
    });
  }
}

export const httpClient = new HttpClient(process.env.REACT_APP_API_VERSION_V1);
export const httpClientV2 = new HttpClient("client");
/** Use for site-area and related APIs that use /v2/ base path (e.g. /v2/api/site-areas/...) */
export const httpClientApiV2 = new HttpClient(
  process.env.REACT_APP_API_VERSION_V2 || "v2"
);
